import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import DOMPurify from "dompurify";
import LoadingSpinner from "./LoadingSpinner";
import SearchBar from "./SearchBar";
import DisputeFilters, { FilterState } from "./DisputeFilters";
import CostDashboard from "./CostDashboard";
import DecisionDisplay from "./DecisionDisplay";
import { useAuth } from "../contexts/AuthContext";
import "./AdminDashboard.css";

interface TokenCost {
  model?: string;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  inputCost: number;
  outputCost: number;
  totalCost: number;
}

interface TokenCosts {
  disputeProcessing: TokenCost;
  total: {
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
    totalCost: number;
  };
}

interface ReasoningStep {
  step: number;
  title: string;
  thought: string;
  conclusion: string;
}

/** Lean payload from /api/admin/reasoning/:id/summary - no prompt/raw blob */
interface ReasoningData {
  disputeId: string;
  timestamp: string;
  inputData: any;
  decision: any;
  reasoningSteps: ReasoningStep[];
  processingTime: number;
  tokenCosts?: TokenCosts;
  isRainDispute?: boolean;
}

/** Heavy debugging payload from /api/admin/reasoning/:id/raw - raw tab only */
interface RawReasoningData {
  disputeId: string;
  promptSent: string;
  aiResponse: any;
}

interface DisputeSummary {
  disputeId: string;
  timestamp: string;
  category: string;
  claimantType: string;
  confidence: number;
  status: string;
  amount: number;
  userId?: string;
  isRainDispute: boolean;
  // Rain API specific fields
  question?: string;
  options?: string[];
  selected_option?: number;
  selected_option_text?: string;
  ai_decision?: string;
  // Token cost information
  tokenCosts?: TokenCosts;
}

interface PaginationInfo {
  current_page: number;
  per_page: number;
  total_pages: number;
  total_items: number;
}

interface DisputeCounts {
  all: number;
  rain: number;
  tomi: number;
}

interface EvidenceItem {
  type: string;
  description: string;
  source: string;
  timestamp: string;
  fileName?: string;
  sha256_hash: string;
  cloudinaryUrl?: string;
  cloudinaryPublicId?: string;
  cloudinaryFormat?: string;
  fileSize?: number;
  aiAnalysis?: string;
  analysisConfidence?: number;
  extractedText?: string;
  analysisSuccess?: boolean;
  tokenCost?: TokenCost;
  youtubeUrl?: string;
}

type DisputeType = "all" | "rain" | "tomi" | "costs";
type DetailTab = "overview" | "evidence" | "analysis" | "raw";

const AdminDashboard: React.FC = () => {
  const { token, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // State
  const [disputes, setDisputes] = useState<DisputeSummary[]>([]);
  const [selectedDispute, setSelectedDispute] = useState<string | null>(null);
  const [reasoningData, setReasoningData] = useState<ReasoningData | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [evidenceItems, setEvidenceItems] = useState<EvidenceItem[] | null>(
    null,
  );
  const [evidenceLoading, setEvidenceLoading] = useState(false);
  const [rawData, setRawData] = useState<RawReasoningData | null>(null);
  const [rawLoading, setRawLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<DetailTab>("overview");

  // Per-dispute caches - browsing back and forth never refetches
  const summaryCache = React.useRef<Map<string, ReasoningData>>(new Map());
  const evidenceCache = React.useRef<Map<string, EvidenceItem[]>>(new Map());
  const rawCache = React.useRef<Map<string, RawReasoningData>>(new Map());

  // Fit the list to the viewport so the sidebar never scrolls internally:
  // ~504px of fixed chrome (nav, tabs, search, filters, list header, pager),
  // ~74px per single-line row including gap - calibrated by measurement.
  const listPageSize = React.useMemo(
    () => Math.min(12, Math.max(4, Math.floor((window.innerHeight - 504) / 74))),
    [],
  );

  // Filter and search state
  const [disputeType, setDisputeType] = useState<DisputeType>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState<FilterState>({
    dateFrom: "",
    dateTo: "",
    minConfidence: 0,
    maxConfidence: 100,
    statuses: [],
    categories: [],
    minAmount: "",
    maxAmount: "",
  });
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);

  // Pagination
  const [pagination, setPagination] = useState<PaginationInfo>({
    current_page: 1,
    per_page: 20,
    total_pages: 1,
    total_items: 0,
  });
  const [counts, setCounts] = useState<DisputeCounts>({
    all: 0,
    rain: 0,
    tomi: 0,
  });

  const fetchDisputes = useCallback(
    async (page: number = 1) => {
      if (!token) {
        console.error("No authentication token available");
        return;
      }

      try {
        const params: any = {
          page,
          limit: listPageSize,
          disputeType,
        };

        // Add filters
        if (filters.dateFrom) params.dateFrom = filters.dateFrom;
        if (filters.dateTo) params.dateTo = filters.dateTo;
        if (filters.minConfidence > 0)
          params.minConfidence = filters.minConfidence / 100;
        if (filters.maxConfidence < 100)
          params.maxConfidence = filters.maxConfidence / 100;
        if (filters.statuses.length > 0)
          params.statuses = JSON.stringify(filters.statuses);
        if (filters.categories.length > 0)
          params.categories = JSON.stringify(filters.categories);

        const response = await api.get(`/api/admin/disputes`, {
          params,
        });

        setDisputes(response.data.disputes || []);
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }
        if (response.data.counts) {
          setCounts(response.data.counts);
        }

        // Extract unique categories for filter dropdown
        const categoriesSet = new Set(
          response.data.disputes
            .map((d: DisputeSummary) => d.category)
            .filter(Boolean),
        );
        const categories = Array.from(categoriesSet) as string[];
        setAvailableCategories(categories);
      } catch (error) {
        console.error("Error fetching disputes:", error);
      }
    },
    [token, disputeType, filters, listPageSize],
  );

  const handleSearch = useCallback(
    async (query: string) => {
      if (!token) return;

      setSearchQuery(query);

      if (!query.trim()) {
        fetchDisputes(1);
        return;
      }

      try {
        const response = await api.get(`/api/admin/disputes/search`, {
          params: {
            q: query,
            page: 1,
            limit: listPageSize,
            disputeType,
          },
        });

        setDisputes(response.data.disputes || []);
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }
      } catch (error) {
        console.error("Error searching disputes:", error);
      }
    },
    [token, disputeType, fetchDisputes, listPageSize],
  );

  const fetchEvidence = useCallback(
    async (disputeId: string) => {
      if (!token) return;
      const cached = evidenceCache.current.get(disputeId);
      if (cached) {
        setEvidenceItems(cached);
        return;
      }

      setEvidenceLoading(true);
      try {
        const response = await api.get(
          `/api/admin/disputes/${disputeId}/evidence`,
        );
        const items = response.data.evidence || [];
        evidenceCache.current.set(disputeId, items);
        setEvidenceItems(items);
      } catch (error) {
        console.error("Error fetching evidence:", error);
        setEvidenceItems([]);
      } finally {
        setEvidenceLoading(false);
      }
    },
    [token],
  );

  const fetchRaw = useCallback(
    async (disputeId: string) => {
      if (!token) return;
      const cached = rawCache.current.get(disputeId);
      if (cached) {
        setRawData(cached);
        return;
      }

      setRawLoading(true);
      try {
        const response = await api.get(`/api/admin/reasoning/${disputeId}/raw`);
        rawCache.current.set(disputeId, response.data);
        setRawData(response.data);
      } catch (error) {
        console.error("Error fetching raw reasoning:", error);
      } finally {
        setRawLoading(false);
      }
    },
    [token],
  );

  const selectDispute = async (disputeId: string) => {
    if (!token || disputeId === selectedDispute) return;

    setSelectedDispute(disputeId);
    setActiveTab("overview");
    // Show whatever is already cached for this dispute; heavy tabs stay lazy
    setEvidenceItems(evidenceCache.current.get(disputeId) || null);
    setRawData(rawCache.current.get(disputeId) || null);

    const cached = summaryCache.current.get(disputeId);
    if (cached) {
      setReasoningData(cached);
      return;
    }

    setLoading(true);
    setReasoningData(null);
    try {
      const response = await api.get(
        `/api/admin/reasoning/${disputeId}/summary`,
      );
      summaryCache.current.set(disputeId, response.data);
      setReasoningData(response.data);
    } catch (error) {
      console.error("Error fetching reasoning data:", error);
    } finally {
      setLoading(false);
    }
  };

  const openTab = (tab: DetailTab) => {
    setActiveTab(tab);
    if (!selectedDispute) return;
    if (tab === "evidence") fetchEvidence(selectedDispute);
    if (tab === "raw") fetchRaw(selectedDispute);
  };

  /** Restore needs inputData only - reuse the cached summary when we have it */
  const getInputData = async (disputeId: string) => {
    const cached = summaryCache.current.get(disputeId);
    if (cached) return cached.inputData;
    const response = await api.get(`/api/admin/reasoning/${disputeId}/summary`);
    summaryCache.current.set(disputeId, response.data);
    return response.data.inputData;
  };

  const handleRestoreRainQuestion = async (
    e: React.MouseEvent,
    dispute: DisputeSummary,
  ) => {
    e.stopPropagation();

    try {
      const inputData = await getInputData(dispute.disputeId);

      navigate("/rain", {
        state: {
          restoreData: {
            question: dispute.question || "",
            options: dispute.options || ["", ""],
            context:
              inputData?.statement_of_claim || inputData?.request_details || "",
            poolCreationDate: inputData?.pool_creation_date
              ? new Date(inputData.pool_creation_date)
                  .toISOString()
                  .split("T")[0]
              : "",
            aiDecision: dispute.ai_decision || "",
            evidence: inputData?.submitted_evidence || [],
          },
        },
      });
    } catch (error) {
      console.error("Error fetching dispute details:", error);
      alert("Failed to restore question. Please try again.");
    }
  };

  const handleRestoreTomiDispute = async (
    e: React.MouseEvent,
    dispute: DisputeSummary,
  ) => {
    e.stopPropagation();

    try {
      const inputData = await getInputData(dispute.disputeId);

      navigate("/dispute", {
        state: {
          restoreData: {
            claimantType: dispute.claimantType || "Buyer",
            disputeAmount: dispute.amount || 0,
            statementOfClaim: inputData?.statement_of_claim || "",
            statementOfDefence: inputData?.statement_of_defence || "",
            disputeCategory: dispute.category || "",
            requestDetails: inputData?.request_details || "",
            evidence: inputData?.submitted_evidence || [],
          },
        },
      });
    } catch (error) {
      console.error("Error fetching dispute details:", error);
      alert("Failed to restore dispute. Please try again.");
    }
  };

  useEffect(() => {
    if (isAuthenticated && token && disputeType !== "costs") {
      fetchDisputes();
    }
  }, [isAuthenticated, token, disputeType, fetchDisputes]);

  const handleTypeChange = (type: DisputeType) => {
    setDisputeType(type);
    setSelectedDispute(null);
    setReasoningData(null);
  };

  const handleApplyFilters = (newFilters: FilterState) => {
    setFilters(newFilters);
    setSelectedDispute(null);
    setReasoningData(null);
  };

  const getConfidenceColor = (score: number) => {
    if (score >= 0.75) return "#2e7d32";
    if (score >= 0.6) return "#9a7d2e";
    return "#b3502c";
  };

  if (!isAuthenticated) {
    return (
      <div className="admin-dashboard">
        <div className="dashboard-layout">
          <div className="empty-state">
            <h3>Authentication Required</h3>
            <p>Please log in to access the admin dashboard.</p>
          </div>
        </div>
      </div>
    );
  }

  // The reasoning endpoint doesn't return an isRainDispute flag, so derive it
  // the same way the server classifies: a non-empty question or the
  // options-selection category means Rain.
  const isRainDispute = !!(
    reasoningData?.isRainDispute ||
    (typeof reasoningData?.inputData?.question === "string" &&
      reasoningData.inputData.question.trim()) ||
    reasoningData?.inputData?.dispute_category === "options_selection"
  );

  return (
    <div className="admin-dashboard">
      <LoadingSpinner isVisible={loading} />

      <div className="dashboard-header">
        {/* Dispute Type Tabs */}
        <div className="dispute-type-tabs">
          <button
            className={`type-tab ${disputeType === "all" ? "active" : ""}`}
            onClick={() => handleTypeChange("all")}
          >
            All
            <span className="tab-count">{counts.all}</span>
          </button>
          <button
            className={`type-tab rain-tab ${
              disputeType === "rain" ? "active" : ""
            }`}
            onClick={() => handleTypeChange("rain")}
          >
            Rain
            <span className="tab-count">{counts.rain}</span>
          </button>
          <button
            className={`type-tab tomi-tab ${
              disputeType === "tomi" ? "active" : ""
            }`}
            onClick={() => handleTypeChange("tomi")}
          >
            Tomi
            <span className="tab-count">{counts.tomi}</span>
          </button>
          <button
            className={`type-tab costs-tab ${
              disputeType === "costs" ? "active" : ""
            }`}
            onClick={() => handleTypeChange("costs")}
          >
            Costs
          </button>
        </div>

        {/* Search Bar - Hide on Costs tab */}
        {disputeType !== "costs" && (
          <div className="dashboard-search">
            <SearchBar
              onSearch={handleSearch}
              placeholder="Search disputes by ID, question, category, or content..."
              isLoading={loading}
            />
          </div>
        )}

        {/* Advanced Filters - Hide on Costs tab */}
        {disputeType !== "costs" && (
          <DisputeFilters
            onApply={handleApplyFilters}
            availableCategories={availableCategories}
            isOpen={isFiltersOpen}
            onToggle={() => setIsFiltersOpen(!isFiltersOpen)}
          />
        )}
      </div>

      {/* Costs Tab View */}
      {disputeType === "costs" ? (
        <div className="costs-tab-content">
          <CostDashboard
            token={token || ""}
            disputeType="all"
            dateFrom={filters.dateFrom}
            dateTo={filters.dateTo}
          />
        </div>
      ) : (
        /* Disputes View */
        <div className="dashboard-layout">
          {/* Sidebar - Dispute List */}
          <aside className="disputes-sidebar">
            <h2>{searchQuery ? "Search Results" : "Recent Disputes"}</h2>
            <div className="disputes-list">
              {disputes.map((dispute) => (
                <div
                  key={dispute.disputeId}
                  className={`dispute-item ${
                    selectedDispute === dispute.disputeId ? "active" : ""
                  } ${dispute.isRainDispute ? "rain-dispute" : "tomi-dispute"}`}
                  onClick={() => selectDispute(dispute.disputeId)}
                >
                  <div className="dispute-row-main">
                    <span
                      className="dispute-question"
                      title={
                        dispute.isRainDispute ? dispute.question : undefined
                      }
                    >
                      {dispute.isRainDispute
                        ? dispute.question
                        : dispute.category || "Tomi dispute"}
                    </span>
                    <button
                      className="dispute-restore-btn"
                      onClick={(e) =>
                        dispute.isRainDispute
                          ? handleRestoreRainQuestion(e, dispute)
                          : handleRestoreTomiDispute(e, dispute)
                      }
                      title={
                        dispute.isRainDispute
                          ? "Restore this question in the wizard"
                          : "Restore this dispute in the Tomi form"
                      }
                    >
                      Restore
                    </button>
                  </div>
                  <div className="dispute-row-meta">
                    <span
                      className="confidence-dot"
                      style={{
                        backgroundColor: getConfidenceColor(dispute.confidence),
                      }}
                    />
                    <span className="dispute-outcome">
                      {dispute.isRainDispute
                        ? dispute.selected_option_text ||
                          (dispute.selected_option !== undefined &&
                          dispute.selected_option !== null &&
                          dispute.selected_option >= 0
                            ? `Option ${dispute.selected_option}`
                            : "No decision")
                        : dispute.status}
                    </span>
                    <span className="dispute-meta-sep">·</span>
                    <span>{(dispute.confidence * 100).toFixed(0)}%</span>
                    {dispute.tokenCosts && (
                      <>
                        <span className="dispute-meta-sep">·</span>
                        <span>
                          ${dispute.tokenCosts.total.totalCost.toFixed(3)}
                        </span>
                      </>
                    )}
                    <span className="dispute-row-date">
                      {new Date(dispute.timestamp).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}

              {disputes.length === 0 && (
                <p className="no-evidence">No disputes in this view.</p>
              )}

              {pagination.total_pages > 1 && (
                <div className="pagination">
                  <button
                    onClick={() => fetchDisputes(pagination.current_page - 1)}
                    disabled={pagination.current_page <= 1}
                    className="pagination-btn"
                  >
                    Previous
                  </button>
                  <span className="pagination-info">
                    Page {pagination.current_page} of {pagination.total_pages}
                  </span>
                  <button
                    onClick={() => fetchDisputes(pagination.current_page + 1)}
                    disabled={pagination.current_page >= pagination.total_pages}
                    className="pagination-btn"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </aside>

          {/* Main Content - Accordion View */}
          <main className="reasoning-main">
            {!loading && !reasoningData && (
              <div className="empty-state">
                <h3>Select a dispute to view details</h3>
                <p>
                  Click on any dispute from the list to see the full analysis
                </p>
              </div>
            )}

            {!loading && reasoningData && (
              <div className="dispute-details-container">
                {/* Sticky head - strip + tabs stay put while content scrolls */}
                <div className="detail-head">
                {/* Compact summary strip - everything at a glance, no scrolling */}
                <div className="detail-strip">
                  <div className="detail-strip-title">
                    <span
                      className={`type-badge ${
                        isRainDispute ? "rain-badge" : "tomi-badge"
                      }`}
                    >
                      {isRainDispute ? "Rain" : "Tomi"}
                    </span>
                    <h2>{reasoningData.disputeId}</h2>
                  </div>
                  <div className="detail-strip-chips">
                    <span className="detail-chip detail-chip-decision">
                      {isRainDispute
                        ? reasoningData.decision?.selected_option_text ||
                          "No decision"
                        : reasoningData.decision?.remedy_awarded?.awarded_to ||
                          "N/A"}
                    </span>
                    <span
                      className="detail-chip"
                      style={{
                        color: getConfidenceColor(
                          reasoningData.decision?.confidence_score || 0,
                        ),
                      }}
                    >
                      {(
                        (reasoningData.decision?.confidence_score || 0) * 100
                      ).toFixed(0)}
                      % confidence
                    </span>
                    {reasoningData.tokenCosts && (
                      <span
                        className="detail-chip detail-chip-cost"
                        title={`Dispute processing: $${(
                          reasoningData.tokenCosts.disputeProcessing
                            ?.totalCost || 0
                        ).toFixed(4)}\nEvidence analysis: $${(
                          (reasoningData.tokenCosts.total?.totalCost || 0) -
                          (reasoningData.tokenCosts.disputeProcessing
                            ?.totalCost || 0)
                        ).toFixed(4)}\nTokens: ${(
                          reasoningData.tokenCosts.total?.totalTokens || 0
                        ).toLocaleString()}`}
                      >
                        $
                        {(
                          reasoningData.tokenCosts.total?.totalCost || 0
                        ).toFixed(4)}
                      </span>
                    )}
                    <span className="detail-chip detail-chip-muted">
                      {new Date(reasoningData.timestamp).toLocaleString()}
                    </span>
                    {reasoningData.processingTime > 0 && (
                      <span className="detail-chip detail-chip-muted">
                        {(reasoningData.processingTime / 1000).toFixed(2)}s
                      </span>
                    )}
                  </div>
                </div>

                {/* Detail tabs - one focused screen per concern */}
                <div className="detail-tabs">
                  <button
                    className={`detail-tab ${
                      activeTab === "overview" ? "active" : ""
                    }`}
                    onClick={() => openTab("overview")}
                  >
                    Overview
                  </button>
                  <button
                    className={`detail-tab ${
                      activeTab === "evidence" ? "active" : ""
                    }`}
                    onClick={() => openTab("evidence")}
                  >
                    Evidence
                    {evidenceItems !== null && ` (${evidenceItems.length})`}
                  </button>
                  <button
                    className={`detail-tab ${
                      activeTab === "analysis" ? "active" : ""
                    }`}
                    onClick={() => openTab("analysis")}
                  >
                    Analysis
                  </button>
                  <button
                    className={`detail-tab ${
                      activeTab === "raw" ? "active" : ""
                    }`}
                    onClick={() => openTab("raw")}
                  >
                    Raw data
                  </button>
                </div>
                </div>

                {/* ---------- Overview ---------- */}
                {activeTab === "overview" && (
                  <div className="detail-panel">
                    <div className="overview-columns">
                      <div>
                        {isRainDispute ? (
                          <div className="rain-resolution">
                            <div className="resolution-field">
                              <label>Question:</label>
                              <div className="rain-question-box">
                                {reasoningData.inputData?.question ||
                                  reasoningData.decision?.question}
                              </div>
                            </div>

                            <div className="resolution-field">
                              <label>Options:</label>
                              <div className="rain-options-list">
                                {(
                                  reasoningData.inputData?.options ||
                                  reasoningData.decision?.options
                                )?.map((option: string, idx: number) => (
                                  <div
                                    key={idx}
                                    className={`rain-option-item ${
                                      idx ===
                                      reasoningData.decision?.selected_option
                                        ? "selected"
                                        : ""
                                    }`}
                                  >
                                    <span className="option-index">
                                      Option {idx}:
                                    </span>
                                    <span className="option-content">
                                      {option}
                                    </span>
                                    {idx ===
                                      reasoningData.decision
                                        ?.selected_option && (
                                      <span className="selected-checkmark">
                                        Selected
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>

                            {reasoningData.inputData?.request_details && (
                              <div className="resolution-field">
                                <label>Context:</label>
                                <p className="detail-context-text">
                                  {reasoningData.inputData.request_details}
                                </p>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="tomi-resolution">
                            <div className="resolution-grid">
                              <div className="resolution-field">
                                <label>Winner:</label>
                                <span className="winner-badge">
                                  {reasoningData.decision?.remedy_awarded
                                    ?.awarded_to || "N/A"}
                                </span>
                              </div>
                              <div className="resolution-field">
                                <label>Remedy Amount:</label>
                                <span className="amount-value">
                                  $
                                  {reasoningData.decision?.remedy_awarded?.amount_usd?.toFixed(
                                    2,
                                  ) || "0.00"}
                                </span>
                              </div>
                              <div className="resolution-field">
                                <label>Remedy Type:</label>
                                <span>
                                  {reasoningData.decision?.remedy_awarded
                                    ?.type || "N/A"}
                                </span>
                              </div>
                              <div className="resolution-field">
                                <label>Return Required:</label>
                                <span>
                                  {reasoningData.decision?.remedy_awarded
                                    ?.return_required
                                    ? "Yes"
                                    : "No"}
                                </span>
                              </div>
                            </div>
                            <div className="claim-section">
                              <label>Statement of Claim:</label>
                              <p>
                                {reasoningData.inputData?.statement_of_claim ||
                                  "No statement of claim provided"}
                              </p>
                            </div>
                            {reasoningData.inputData?.statement_of_defence && (
                              <div className="claim-section">
                                <label>Statement of Defence:</label>
                                <p>
                                  {reasoningData.inputData.statement_of_defence}
                                </p>
                              </div>
                            )}
                          </div>
                        )}

                        {reasoningData.decision?.rules_applied && (
                          <div className="resolution-rules">
                            <label>Rules Applied:</label>
                            <div className="rules-list">
                              {reasoningData.decision.rules_applied.map(
                                (rule: string, idx: number) => (
                                  <span key={idx} className="rule-tag">
                                    {rule}
                                  </span>
                                ),
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="resolution-findings">
                          <label>Key Findings:</label>
                          <div
                            className={`findings-text ${
                              isRainDispute ? "findings-html" : ""
                            }`}
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(
                                reasoningData.decision?.finding_summary ||
                                  "No findings available",
                              ),
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ---------- Evidence (lazy) ---------- */}
                {activeTab === "evidence" && (
                  <div className="detail-panel">
                    {evidenceLoading ? (
                      <div className="loading-text">Loading evidence...</div>
                    ) : !evidenceItems || evidenceItems.length === 0 ? (
                      <div className="no-evidence">
                        No evidence files found for this dispute.
                      </div>
                    ) : (
                      <div className="evidence-grid">
                        {evidenceItems.map((evidence, index) => (
                          <div key={index} className="evidence-card">
                            <div className="evidence-card-header">
                              <h4>
                                {evidence.type.replace(/_/g, " ").toUpperCase()}
                              </h4>
                              <span className="evidence-source">
                                {evidence.source}
                              </span>
                            </div>

                            <div className="evidence-card-body">
                              {evidence.tokenCost && (
                                <div className="evidence-cost-badge">
                                  <div className="cost-info">
                                    <div className="cost-amount">
                                      ${evidence.tokenCost.totalCost.toFixed(4)}
                                    </div>
                                    <div className="cost-tokens">
                                      {evidence.tokenCost.totalTokens.toLocaleString()}{" "}
                                      tokens
                                    </div>
                                  </div>
                                  {evidence.tokenCost.model && (
                                    <div className="cost-model">
                                      {evidence.tokenCost.model}
                                    </div>
                                  )}
                                </div>
                              )}

                              <div className="evidence-field">
                                <label>Description:</label>
                                <p>{evidence.description}</p>
                              </div>

                              {evidence.fileName && (
                                <div className="evidence-field">
                                  <label>File:</label>
                                  <p className="file-name">
                                    {evidence.fileName}
                                  </p>
                                </div>
                              )}

                              {evidence.youtubeUrl && (
                                <div className="evidence-field">
                                  <label>YouTube URL:</label>
                                  <p className="youtube-url">
                                    {evidence.youtubeUrl}
                                  </p>
                                </div>
                              )}

                              {evidence.cloudinaryUrl && (
                                <div className="evidence-preview">
                                  {evidence.type === "photo" && (
                                    <img
                                      src={evidence.cloudinaryUrl}
                                      alt={evidence.description}
                                      className="evidence-image"
                                    />
                                  )}
                                  {evidence.type === "pdf" && (
                                    <iframe
                                      src={evidence.cloudinaryUrl}
                                      title={evidence.fileName}
                                      className="evidence-pdf"
                                    />
                                  )}
                                  <a
                                    href={evidence.cloudinaryUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="evidence-link"
                                  >
                                    View/Download File
                                  </a>
                                </div>
                              )}

                              {evidence.aiAnalysis && (
                                <div className="evidence-analysis">
                                  <label>AI Analysis:</label>
                                  {evidence.analysisSuccess && (
                                    <div className="analysis-confidence">
                                      Confidence:{" "}
                                      {(
                                        (evidence.analysisConfidence || 0) * 100
                                      ).toFixed(0)}
                                      %
                                    </div>
                                  )}
                                  <p>{evidence.aiAnalysis}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* ---------- Analysis ---------- */}
                {activeTab === "analysis" && (
                  <div className="detail-panel">
                    {isRainDispute &&
                      (reasoningData.decision?.gpt_analysis ||
                        reasoningData.decision?.perplexity_analysis) && (
                        <div className="agents-comparison">
                          <label>Research agents:</label>
                          {reasoningData.decision
                            ?.final_analysis_of_the_two_analyses && (
                            <p className="agents-synthesis">
                              {
                                reasoningData.decision
                                  .final_analysis_of_the_two_analyses
                              }
                            </p>
                          )}
                          <div className="agents-grid">
                            {reasoningData.decision?.gpt_analysis && (
                              <div className="agent-column">
                                <div className="agent-column-title">
                                  GPT agent
                                </div>
                                <div
                                  className="agent-column-body"
                                  dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(
                                      reasoningData.decision.gpt_analysis,
                                    ),
                                  }}
                                />
                              </div>
                            )}
                            {reasoningData.decision?.perplexity_analysis && (
                              <div className="agent-column">
                                <div className="agent-column-title">
                                  Perplexity agent
                                </div>
                                <div
                                  className="agent-column-body"
                                  dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(
                                      reasoningData.decision
                                        .perplexity_analysis,
                                    ),
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                    {reasoningData.reasoningSteps &&
                      reasoningData.reasoningSteps.length > 0 && (
                        <div className="reasoning-steps">
                          {reasoningData.reasoningSteps.map((step) => (
                            <div key={step.step} className="reasoning-step">
                              <div className="step-header">
                                <span className="step-number">
                                  Step {step.step}
                                </span>
                                <h3>{step.title}</h3>
                              </div>
                              <div className="step-content">
                                <div className="thought-bubble">
                                  <h4>AI Thought Process:</h4>
                                  <p>{step.thought}</p>
                                </div>
                                <div className="conclusion-box">
                                  <h4>Conclusion:</h4>
                                  <p>{step.conclusion}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                    {reasoningData.tokenCosts && (
                      <div className="cost-tracking-section">
                        <h3>Token Cost Tracking</h3>
                        <div className="cost-grid">
                          <div className="cost-card total-cost">
                            <h4>Total Cost</h4>
                            <div className="cost-value">
                              $
                              {reasoningData.tokenCosts.total.totalCost.toFixed(
                                4,
                              )}
                            </div>
                            <div className="cost-details">
                              <span>
                                {reasoningData.tokenCosts.total.totalTokens.toLocaleString()}{" "}
                                tokens
                              </span>
                            </div>
                          </div>
                          <div className="cost-card processing-cost">
                            <h4>Dispute Processing</h4>
                            <div className="cost-value">
                              $
                              {reasoningData.tokenCosts.disputeProcessing.totalCost.toFixed(
                                4,
                              )}
                            </div>
                            <div className="cost-details">
                              <span>
                                Model:{" "}
                                {reasoningData.tokenCosts.disputeProcessing
                                  .model || "N/A"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {!isRainDispute &&
                      (!reasoningData.reasoningSteps ||
                        reasoningData.reasoningSteps.length === 0) &&
                      !reasoningData.tokenCosts && (
                        <div className="no-evidence">
                          No analysis data available for this dispute.
                        </div>
                      )}
                  </div>
                )}

                {/* ---------- Raw data (lazy, heavy) ---------- */}
                {activeTab === "raw" && (
                  <div className="detail-panel">
                    {rawLoading ? (
                      <div className="loading-text">Loading raw data...</div>
                    ) : !rawData ? (
                      <div className="no-evidence">
                        Raw data is not available for this dispute.
                      </div>
                    ) : (
                      <div className="technical-details">
                        <div className="technical-field">
                          <label>Dispute ID:</label>
                          <code>{reasoningData.disputeId}</code>
                        </div>

                        <div className="technical-section">
                          <h4>Full Prompt Sent to AI:</h4>
                          <pre className="code-block">{rawData.promptSent}</pre>
                        </div>

                        <div className="technical-section">
                          <h4>Raw AI Response:</h4>
                          <pre className="code-block">
                            {JSON.stringify(rawData.aiResponse, null, 2)}
                          </pre>
                        </div>

                        <div className="technical-section">
                          <h4>Formatted Decision:</h4>
                          <div className="decision-container">
                            <DecisionDisplay decision={rawData.aiResponse} />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </main>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
